/**
 * 未回答の DLE 評価アンケートをまとめて回答するボタン（dev / design view 切替の隣）。
 *
 * ボタン自体は未回答が 2 件以上のときだけ出すが、**コンポーネントは常にマウントする**。
 * Slack DM の deep link の自動オープンをここが担っており、未回答が 1 件のときに
 * まるごと描画をやめると DM のリンクが動かなくなるため。デザイン view で隠すのも
 * 同じ理由で `visible` によるボタンの出し分けにしており、マウント自体は止めない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 未回答 DLE 評価アンケートの一括回答ボタンと Slack DM deep link の自動オープン
 * @graph-connects useMyDleTasks [calls] DLE 評価タスク一覧取得
 * @graph-connects DleSurveyModal [calls] 回答モーダル表示
 */
import { useEffect, useMemo, useState } from 'react';

import { DleSurveyModal } from './DleSurveyModal.js';
import { orderedDleTasksFrom } from './dle-survey-rows.js';
import { useMyDleTasks } from '../../../hooks/useDleEvaluationSurvey.js';
import type { DleEvaluationTask } from '../../../types/dle-evaluation.js';

interface DleSurveyBatchAnswerButtonProps {
  /**
   * Slack DM の deep link (`/my-page?dleSurvey=...`) で指定された母票キー。
   * 一致する未回答があれば、その件を先頭にしてモーダルを自動オープンする。
   */
  autoOpenParentIssueKey?: string;
  /**
   * ボタンを出すか。DLE 評価の列は開発 view のテーブルにしか無いので、
   * デザイン view では `false` にして出さない（deep link のためマウントは続ける）。
   * 既定は `true`。
   */
  visible?: boolean;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 一括回答ボタン本体。未回答件数バッジ付きの pill ボタン（開発 view のアクセント色）とモーダル起動を担う
 * @graph-connects useMyDleTasks [calls] DLE 評価タスク一覧取得
 * @graph-connects DleSurveyModal [calls] 回答モーダル表示
 */
export function DleSurveyBatchAnswerButton({
  autoOpenParentIssueKey,
  visible = true,
}: DleSurveyBatchAnswerButtonProps) {
  const { data } = useMyDleTasks();
  const [modalTasks, setModalTasks] = useState<DleEvaluationTask[] | null>(null);
  // deep link は 1 回だけ発火させる（閉じた直後に再オープンしてループしないように）。
  const [autoOpened, setAutoOpened] = useState(false);

  const pending = useMemo(
    () => (data?.tasks ?? []).filter((task) => task.state === 'pending'),
    [data],
  );

  useEffect(() => {
    if (autoOpened || autoOpenParentIssueKey === undefined) return;
    const index = pending.findIndex((task) => task.parentIssueKey === autoOpenParentIssueKey);
    if (index < 0) return;
    setModalTasks(orderedDleTasksFrom(pending, index));
    setAutoOpened(true);
  }, [autoOpenParentIssueKey, autoOpened, pending]);

  return (
    <>
      {visible && pending.length > 1 && (
        <button
          type="button"
          onClick={() => {
            setModalTasks(pending);
          }}
          /* 色は開発 view のアクセント（ライト青 / ダーク黄）に合わせる。 */
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-dev-accent)] px-3.5 py-1.5 text-xs font-medium whitespace-nowrap text-[var(--color-dev-accent-on)] transition-opacity hover:opacity-80"
        >
          DLE 評価をまとめて回答
          <span
            className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--color-warning)] px-1 text-[11px] font-bold text-white"
            aria-label={`未回答の DLE 評価アンケート ${String(pending.length)} 件`}
          >
            {pending.length}
          </span>
        </button>
      )}
      {modalTasks && (
        <DleSurveyModal
          tasks={modalTasks}
          open
          onClose={() => {
            setModalTasks(null);
          }}
        />
      )}
    </>
  );
}
